// A deterministic, offline author avatar: 1-2 initials in white over a color
// derived from the author's email (or name). No network, no Gravatar - the same
// author always renders the same badge, which the History/graph views use to put
// a face next to each commit row without leaking the email over the wire.

import type { CSSProperties, ReactElement } from 'react';

/**
 * Avatar palette. Saturated, mid-dark hues chosen for legible WHITE initials on
 * both the light and dark app surfaces (deliberately distinct from the brighter
 * lane palette, which includes yellows that wash out under white text).
 */
export const AVATAR_COLORS = [
  '#3b6fd4', // blue
  '#2f9e5e', // green
  '#c2701c', // amber
  '#c43a5b', // rose
  '#8a4fd0', // purple
  '#1f9aa0', // teal
  '#5a6b7a', // slate
  '#b0463f', // brick
  '#4f7d2e', // olive
  '#9a4fa8', // orchid
] as const;

/**
 * First letters of up to two name words, uppercased. When the name is empty,
 * falls back to the email local-part, split on `.`/`_`/`-`/`+` so "bob" -> "B"
 * but "ada.lovelace" -> "AL". Final fallback is `'?'`.
 */
export function avatarInitials(name: string, email: string): string {
  const initialsFrom = (parts: string[]): string =>
    parts
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase();

  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length > 0) return initialsFrom(words);

  const local = email.trim().split('@')[0] ?? '';
  const segments = local.split(/[^A-Za-z0-9]+/).filter(Boolean);
  if (segments.length > 0) return initialsFrom(segments);

  return '?';
}

/**
 * Deterministic palette color from a seed: a small string hash of the lowercased
 * seed indexed into {@link AVATAR_COLORS}. Same seed -> same color; differing
 * seeds generally (not guaranteed) differ.
 */
export function avatarColor(seed: string): string {
  const key = seed.trim().toLowerCase();
  // djb2-style hash, kept in the positive 32-bit range.
  let hash = 5381;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 33 + key.charCodeAt(i)) | 0;
  }
  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
}

export interface CommitAvatarProps {
  /** Author display name (used for initials + the accessible label). */
  name: string;
  /** Author email (the primary color seed). */
  email: string;
  /** Diameter in pixels. Defaults to 20. */
  size?: number;
}

/** A circular author badge: white initials centered over a deterministic color. */
export function CommitAvatar({ name, email, size = 20 }: CommitAvatarProps): ReactElement {
  const initials = avatarInitials(name, email);
  const color = avatarColor(email || name);
  const label = name.trim() || email.trim() || 'Unknown author';

  return (
    <span
      className="mp-git-avatar"
      role="img"
      aria-label={label}
      title={label}
      style={{
        '--mp-git-avatar-size': `${size}px`,
        background: color,
      } as CSSProperties}
    >
      {initials}
    </span>
  );
}
